export interface HelpInfo {
  title?: string;
  description?: string;
  children?: HelpInfo[];
  examples?: string;
}

export enum WORKSHOP_HELP {
  WORKSHOP_TYPE = 'workshopType',
  NUMBER_OF_ATTENDEES = 'numberOfAttendees',
  CEIS_SERVICES = 'ceisServices',
}

export enum RESOURCE_HELP {
  NUMBER_OF_VISITS = 'numberOfVisits',
  CEIS_SERVICES = 'ceisServices',
}

export enum DELIVERY_HELP {
  NUMBER_OF_ATTENDEES = 'numberOfAttendees',
  CEIS_SERVICES = 'ceisServices',
  SERVICE_LOCATION = 'serviceLocation',
  EVENT_NAME = 'eventName',
  EVENT_TYPE = 'eventType',
  EVENT_TOPIC = 'eventTopic',
  EVENT_ATTENDEES_HIRED = 'eventAttendeesHired',
  EVENT_VENUE = 'eventVenue',
  EVENT_MUNICIPALITY = 'eventMunicipality',
  PRIMARY_AUDIENCE = 'primaryAudience',
}

export const NumberOfAttendees: HelpInfo = {
  title: 'Number of attendees',
  description:
    'The total number of clients who attended the workshop. For Cohort-based CEIS Series, the number entered is the sum of all the participants for each workshop in the series.',
};

export const CEISServices: HelpInfo = {
  title: 'CEIS services',
  description: 'Select your program/agreement from the list.',
};

export const NumberOfVisits: HelpInfo = {
  title: 'Number of visits',
  description:
    'The number of times a client visited a resource centre in person or contacted the resource centre by phone or electronic mail for similar information. The number of visits to a resource centre website can also be included.',
};

export const EventVenue: HelpInfo = {
  title: 'Venue',
  description:
    'The specific location where the event took place (e.g. online, library, community centre, convention centre, City Hall, etc.).',
};

export const EventMunicipality: HelpInfo = {
  title: 'Municipality',
  description:
    'City, town etc. where the Delivery Event took place. If online, choose the location of the host/planner.',
};

export const EventAttendeesHired: HelpInfo = {
  title: 'Number of attendees hired at event',
  description:
    'The number of job seekers who were offered a job by an Employer at or shortly after participating in the Delivery Event (as reported by the Employer).',
};

export const ServiceLocation: HelpInfo = {
  title: 'Service location',
  description: 'Select your office location from the list.',
};

export const EventName: HelpInfo = {
  title: 'Event name',
  description:
    'Enter the name of the Delivery Event. You should only enter events where you provided the service. Do not enter events that you only attended. Delivery Events may be in person or virtual.',
};

export const EventType: HelpInfo = {
  title: 'Type',
  children: [
    {
      title: 'Community Presentations',
      description:
        'A public talk or presentation that provides employment related information to a group. e.g. open houses, panel discussions, conference or meeting presentations and consultations.',
    },
    {
      title: 'Employer Connections',
      description:
        'Opportunities for employer(s) to connect directly with job seekers to promote and fill job vacancies.',
    },
    {
      title: 'Employer Visits',
      description:
        'One-on-one meetings with employers or industry representatives to discuss Government of Alberta employment programs, services, and labour market needs.',
    },
    {
      title: 'Fair',
      description:
        'Trade-show-style events showcasing career, employment, and education options and services. Events vary in location, duration and cost. e.g. job fairs, resource fairs and career days. Note: only enter Fairs you hosted / organized.',
    },
  ],
};

export const EventTopic: HelpInfo = {
  title: 'Select the primary topic or purpose of the Delivery Event',
  children: [
    {
      title: 'Career Services',
      description:
        'Helps participants clarify occupational and career goals, learn more about the labour market and make informed career decisions.',
    },
    {
      title: 'Employment Services',
      description: 'Helps participants to prepare for, find, and maintain employment.',
    },
    {
      title: 'Information Services',
      description: 'Provides direction on where participants can access government or community services or resources.',
    },
    {
      title: 'Skill Development',
      description:
        'Provides information to participants on where or how to access training or further education as part of finding work or improving work related skills. ',
    },
    {
      title: 'Support Financial',
      description:
        'Provides information to participants on where or how to access financial assistance to meet basic needs.',
    },
    {
      title: 'Support Non-Financial',
      description:
        'Provides information on participant supports related to personal, medical and/or family. (e.g. mental health services, parenting supports, housing assistance, addictions support).',
    },
  ],
};

export const WorkshopType: HelpInfo = {
  title: 'Workshop Types',
  description:
    'Workshops are a group service delivered to Albertans to address a variety of career and employment needs. They have a specific start and end date.',
  children: [
    {
      title: 'Category - Career Planning',
      description: 'What type of work do I want to look for?',
      examples:
        'self-assessment, career planning, education planning, exploring self-employment, labour market information',
    },
    {
      title: 'Category - Cohort-based CEIS Series',
      description:
        'A series of workshops delivered to a defined group of clients. Note: the number entered is the sum of all the participants for each workshop in the series.',
      examples: 'job club, career planning series',
    },
    {
      title: 'Category - Employability Skills',
      description: 'What skills will I need once I am working?',
      examples:
        'workplace culture, employer expectations, time management, skills for success/essential skills, communication skills, resolving conflict',
    },
    {
      title: 'Category - Job Search Skills',
      description: 'How do I look for work?',
      examples: 'job search skills, resume and cover letters, interview skills, online job search, Networking',
    },
    {
      title: 'Category - Personal Skills',
      description: 'How do I take care of myself as I look for work and once I am working?',
      examples:
        'mental health, resiliency, wellness, stress management, financial literacy and budgeting, digital skills',
    },
  ],
};
const PrimaryAudience: HelpInfo = {
  title: 'Choose based on the target audience or the purpose or goal of the Delivery Event',
  children: [
    {
      title: 'Albertans with Disabilities',
      description: 'individuals who self-identify as having a disability or impairment that limits their activities.',
    },
    {
      title: 'Community Agency(s)',
      description:
        'a public or private organization that provides services to improve the health, social, mental health, or well-being of a community.',
    },
    {
      title: 'Employers',
      description:
        'individuals who are in a position to hire or assist in the hiring of employees for their organization.',
    },
    {
      title: 'External Service Provider',
      description: 'employees of public or private organizations that provide services on behalf of the ministry.',
    },
    { title: 'Educators', description: 'individuals who work in the education sector.' },
    { title: 'French speaking Albertans', description: 'individuals who are served in French.' },
    {
      title: 'Gender Diverse',
      description:
        'individuals who self identify as having a gender identity or expression that differs from the traditional or societal expectations of the male-female binary.',
    },
    { title: 'General Public', description: 'open to general public; no particular target group.' },
    { title: 'GoA Staff', description: 'employees of the Government of Alberta.' },
    { title: 'Immigrants', description: 'individuals who have moved to Canada from another country.' },
    {
      title: 'Indigenous Persons',
      description: 'individuals who self-identify as Metis, First Nation or Inuit, and includes non-status.',
    },
    {
      title: 'Industry Sector(s)',
      description: 'representatives of specific industry sectors such as construction, retail or hospitality.',
    },
    { title: 'Job Seekers', description: 'individuals who are looking for employment.' },
    {
      title: 'Low Income Albertans',
      description:
        'Albertans who identify as being in need of financial assistance. Includes, but is not restricted to, IS or AISH program applicants or recipients.',
    },
    { title: 'Mature workers', description: 'individuals between the ages of 50 and 65.' },
    { title: 'Parents', description: 'individuals who have children.' },
    { title: 'Students', description: 'individuals who are enrolled or attending a training or educational program.' },
    { title: 'Women', description: 'individuals who identify as female.' },
    { title: 'Youth', description: 'individuals between the ages of 15 and 24.' },
  ],
};

export const WorkshopHelp: { [name: string]: HelpInfo } = {
  workshopType: WorkshopType,
  numberOfAttendees: NumberOfAttendees,
  ceisServices: CEISServices,
};

export const ResourceHelp: { [name: string]: HelpInfo } = {
  numberOfVisits: NumberOfVisits,
  ceisServices: CEISServices,
};

export const DeliveryHelp: { [name: string]: HelpInfo } = {
  numberOfAttendees: NumberOfAttendees,
  ceisServices: CEISServices,
  serviceLocation: ServiceLocation,
  eventName: EventName,
  eventType: EventType,
  eventTopic: EventTopic,
  eventAttendeesHired: EventAttendeesHired,
  eventVenue: EventVenue,
  eventMunicipality: EventMunicipality,
  primaryAudience: PrimaryAudience,
};
